import React, { useState, useEffect } from 'react';
import { LazyTooltip } from '../Tooltip';
import { Icon } from '../icons/Icon';
import Avatar from '../Avatar';
import type { IntlShape } from 'react-intl';

export interface UserMenuProps {
  intl: IntlShape;
  user?: {
    image?: string;
    picture?: string;
    name?: string;
  } | null;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  className?: string;
}

/**
 * User menu button component for navigation
 * Opens the mobile menu drawer with user options.
 * Defers showing user avatar until after mount so server and client both render the same placeholder (Avatar with no src), avoiding hydration mismatch when user differs between server and client.
 */
export const UserMenu: React.FC<UserMenuProps> = ({
  intl,
  user,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  className = '',
}) => {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  const handleClick = () => {
    const willOpen = !(isMobileMenuOpen ?? false);
    setIsMobileMenuOpen?.(willOpen);
  };

  // Only use user avatar after mount so SSR and first client paint both render Avatar with no src (initials "?") — avoids hydration mismatch.
  const effectiveUser = hasMounted ? user : null;
  const avatarUrl =
    effectiveUser &&
    (effectiveUser.image ??
      effectiveUser.picture ??
      (effectiveUser as { avatar?: string })?.avatar ??
      (effectiveUser as { avatar_url?: string })?.avatar_url);
  const hasUser = !!effectiveUser;

  return (
    <LazyTooltip
      content={intl.formatMessage({
        id: 'tooltip.menu',
        defaultMessage: 'Open menu to access saved jobs, profile, and settings',
        description: 'Tooltip text for menu button that opens user menu drawer',
      })}
      position="bottom"
    >
      <button
        suppressHydrationWarning
        className={`flex items-center gap-2 py-1.5 rounded-full bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
          hasUser ? 'pl-1 pr-2' : 'px-2'
        } ${className}`}
        onClick={handleClick}
        aria-label={intl.formatMessage({
          id: 'navigation.menu',
          defaultMessage: 'Menu',
          description: 'Button aria-label to open navigation menu',
        })}
      >
        {/* Always render Avatar so DOM structure is identical on server and client; pass no src until after mount. */}
        <span className="flex items-center gap-2 flex-shrink-0">
          <span className="flex-shrink-0 overflow-hidden rounded-full w-8 h-8 flex items-center justify-center">
            <Avatar
              src={avatarUrl ?? null}
              size={32}
              name={effectiveUser?.name || 'User'}
            />
          </span>
          <span className="text-gray-700 dark:text-gray-300 flex-shrink-0">
            <Icon name="bars3" useMaskForColor className="w-5 h-5" />
          </span>
        </span>
      </button>
    </LazyTooltip>
  );
};
